import React, { useState } from 'react';
import clsx from 'clsx';
import NotificationCardV2 from '@components/notifications-v2/components/notification-card-v2/notification-card-v2';
import NotificationsLoadingAnimationV2 from '@components/notifications-v2/components/notification-loading-animation-v2/notification-loading-animation-v2';
import NotificationsEmptyListV2 from '@components/notifications-v2/components/notification-empty-list-v2/notification-empty-list-v2';
import logger from '@helpers/utils/logger/client';
import Chat from '@components/organisms/chat/chat';
import useMessageList from '@hooks/chat/use-msg-list';
import { CartContext } from '@context/cart.context';
import { Notification } from '@interfaces/models/notification';
import { useContextSelector } from 'use-context-selector';
import { Product } from '@interfaces/models/product';
import { debounce } from '@helpers/utils/general';
import AddToCartModal from '@components/molecules/products/add-to-cart-modal/add-to-cart-modal';
import { useChat } from '@context/chat.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import {
  useGetActiveNegotiation,
  useGetNegotiationInitialData,
  useGetNegotiationByProductIdAndBuyerId,
} from '@hooks/negotiations/useNegotationQueries';
import { useNegotiation } from '@context/negotiation.context';
import useNotificationFeedQueryV2 from '@hooks/notifications-v2/use-notification-feed-query-v2';
import ChatNotFound from '@components/organisms/chat/chat-not-found/chat-not-found';
import { useTranslation } from 'next-i18next';
import styles from './index.module.scss';

type MessageListProps = {
  product?: Product | null;
  chatId?: string | undefined;
};

const MessageList: React.FC<MessageListProps> = ({ product, chatId }) => {
  const { negotiation, initialDataError } = useNegotiation();
  const { sendAnalyticEvent: sendChatAnalyticEvents } = useAnalyticEvents('chat');
  const { data: messages, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } = useMessageList(60);
  const [isChatClose, setChatClose] = useState(false);
  const addToShoppingCart = useContextSelector(CartContext, (v) => v.addToShoppingCart);
  const { isSeller, buyerId, isProductMissing } = useChat();
  const { isLoading: isLoadingActiveNegotiation, isError: hasActiveNegotiationError } = useGetActiveNegotiation({
    productId: product?.id,
    enabled: product?.negotiable && !isSeller,
  });
  const { data: allNegotiations = [], isLoading: isLoadingListNegotiations } = useGetNegotiationByProductIdAndBuyerId({
    productId: product?.id,
    buyerId,
    enabled: isSeller && buyerId && product?.negotiable,
  });
  const { readNotification } = useNotificationFeedQueryV2('messages', 'inline');

  const negotiationsList = allNegotiations.filter(
    (neg) => neg.product.id === product?.id && !neg.isClosed && neg.buyer.id === buyerId,
  );
  const { t } = useTranslation();

  const isInitialDataAvailable =
    product?.negotiable &&
    !initialDataError &&
    (isSeller
      ? !isLoadingListNegotiations && !negotiationsList.length
      : !isLoadingActiveNegotiation && (hasActiveNegotiationError || negotiation?.isClosed));

  useGetNegotiationInitialData({
    buyerId: isSeller ? buyerId : null,
    productId: product?.id,
    enabled: isInitialDataAvailable,
  });

  const addToCart = async (): Promise<void> => {
    try {
      await addToShoppingCart(product, true);
    } catch (e) {
      logger.info('Error adding to cart - chat page');
    }
  };

  const closeChatHandler = () => {
    setChatClose(true);
  };

  const isActive = (activity: Notification) => {
    return chatId === String(activity?.productLink?.meta?.data?.id);
  };

  const HandleScroll = (e) => {
    if (hasNextPage && !isFetchingNextPage && e.target.scrollTop > e.target.scrollHeight - e.target.offsetHeight - 50) {
      fetchNextPage();
    }
  };

  const onClickOnNotificationCard = () => {
    sendChatAnalyticEvents('bs_chat_change_conversation');
  };

  return (
    <div className={clsx(styles.container, isChatClose && styles.chatOpen, !product && styles.container__noproduct)}>
      <div
        className={styles['left-section']}
        onScroll={debounce(HandleScroll)}
      >
        <div className={styles['left-section__header']}>
          <span className={styles['left-section__header__title']}>{t('CHAT.LIST.MESSAGES')}</span>
        </div>
        {/* render list */}
        <div className={styles['left-section__msg_list']}>
          {messages.map((activity: Notification, index: number) => (
            <NotificationCardV2
              onClick={onClickOnNotificationCard}
              activeStatus={isActive(activity)}
              key={activity.id}
              notification={activity}
              selectedTab={'messages'}
              isChatCell={true}
              readNotification={readNotification}
              position={index}
            />
          ))}
          {!isFetching && messages.length === 0 && <NotificationsEmptyListV2 />}
        </div>
        {isFetching && <NotificationsLoadingAnimationV2 />}
      </div>
      <div className={styles['right-section']}>
        {isProductMissing && <ChatNotFound />}
        {product && (
          <Chat
            mode="inline"
            addToCart={addToCart}
            product={product}
            closeHandler={closeChatHandler}
          />
        )}
      </div>

      <AddToCartModal
        onCloseDialog={() => {
          sendChatAnalyticEvents('bs_chat_popin', {
            label: 'continue_shopping',
          });
        }}
        onRedirectToCheckout={() => {
          sendChatAnalyticEvents('bs_chat_popin', {
            label: 'proceed_to_checkout',
          });
        }}
      />
    </div>
  );
};

export default MessageList;
